import { Socket, SocketConnectOption } from 'phoenix'
import { useEffect, useRef, useState } from 'react'

declare module 'phoenix' {
  interface Socket {
    /**
     * The credential phoenix.js sends as the auth subprotocol. It re-reads this field on
     * every connect attempt, so assigning it is how a live socket picks up a rotated
     * token. Missing from the published typings.
     */
    authToken?: string
  }
}

export interface ManagedSocketOptions extends Partial<SocketConnectOption> {
  /**
   * Supplies a currently valid `authToken` after a connection error, before phoenix.js
   * retries. A socket authenticates only at connect, so an established connection
   * outlives its credential; once the token is rotated — which revokes the previous one
   * — every reconnect is refused, and phoenix.js retries forever with the dead token
   * unless something replaces it here.
   */
  refreshAuthToken?: () => Promise<string | null | undefined>
}

/**
 * Owns a single Phoenix `Socket`: created once, connected while `enabled`, and
 * disconnected on unmount or when disabled. Shared by the socket providers so the
 * connection lifecycle lives in one place.
 */
export const useManagedSocket = (
  url: string,
  options?: ManagedSocketOptions,
  enabled?: boolean
): Socket => {
  const { refreshAuthToken, ...socketOptions } = options ?? {}
  const [socket] = useState(() => new Socket(url, socketOptions))
  const refreshAuthTokenRef = useRef(refreshAuthToken)
  refreshAuthTokenRef.current = refreshAuthToken

  useEffect(() => {
    const ref = socket.onError(async () => {
      try {
        const token = await refreshAuthTokenRef.current?.()
        if (token) socket.authToken = token
      } catch (error) {
        // A refresh that can't complete has nothing better to offer than the token we
        // already hold, and phoenix.js keeps retrying either way. Caught so a rejection
        // inside a phoenix.js callback isn't an unhandled one.
        console.error('[useManagedSocket] auth token refresh failed', error)
      }
    })

    return () => {
      socket.off([ref])
    }
  }, [socket])

  useEffect(() => {
    if (enabled) {
      socket.connect()
    }
    return () => {
      socket.disconnect()
    }
  }, [socket, enabled])

  return socket
}
